import { createContext, useCallback, useContext, useReducer } from 'react';
import useSWR, { preload } from 'swr';
import { connect } from 'react-redux';
import { AUTHORITY, CURRENT_ENV } from '../../constants';
import metadata from '../../metadata.json';
import reducer, { InboxReducerState, InboxTab, initialState } from './reducer';
import { showNotification } from '../../actions';

const env = import.meta.env.VITE_API_ENV || CURRENT_ENV;
const basePath = metadata.environments[env].apiUrl.replace(/\/+$/, '');

type InboxContextValue = InboxReducerState & {
  user: FixMe;
  authorities: string[];
  role: CaregiverRole;
  fetcher(path: string): Promise<FixMe>;
  setFilter(filter: string, reasons: string[]): void;
  toggleSortOrder(section: string): void;
  setHistoryCutoffDays(numDays: number): void;
  toggleViewingAllErrands(): void;
  toggleViewingAllErrandsForRole(): void;
  toggleViewingAllSharedQueueErrands(): void;
  selectCaregiverFilter(caregiver?: SystemUser): void;
  setSectionPage(section: string, page: number): void;
  setLastVisitedTab(tab: InboxTab): void;
  quickSignErrand(patientId: string, encounterId: number): Promise<Response>;
  startEncounter(patientId: string, encounterId: number): Promise<Response>;
  toast(message: string, type?: string, anchorOrigin?: AnchorOrigin): void;
  numActiveErrands: number;
  numActiveAppointments: number;
  numSharedQueueErrands: number;
  authToken?: string;
  clientId?: string;
  renderCaregiverInitials: boolean;
};

export const InboxContext = createContext<InboxContextValue>({
  user: undefined,
  authorities: [],
  role: 'DOCTOR',
  fetcher: () => new Promise((r) => r('')),
  setFilter: () => {},
  toggleSortOrder: () => {},
  setSectionPage: () => {},
  setLastVisitedTab: () => {},
  setHistoryCutoffDays: () => {},
  toggleViewingAllErrands: () => {},
  toggleViewingAllErrandsForRole: () => {},
  toggleViewingAllSharedQueueErrands: () => {},
  selectCaregiverFilter: () => {},
  quickSignErrand: () => new Promise((r) => r(new Response())),
  startEncounter: () => new Promise((r) => r(new Response())),
  toast: () => {},
  numActiveErrands: 0,
  numActiveAppointments: 0,
  numSharedQueueErrands: 0,
  renderCaregiverInitials: false,
  ...initialState
});

const InboxProvider = ({ authToken, clientId, user, role, authorities, showNotification, children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const hasMedicalManagement = authorities.includes(AUTHORITY.MEDICAL_MANAGEMENT);
  const hasNextGenAccess = authorities.includes(AUTHORITY.HANDLE_NEXT_GEN_PATIENTS);

  const fetcher = async (path: string) => {
    const url = `${basePath}${path}`;
    const res = await fetch(url, {
      headers: {
        authorization: `Bearer ${authToken}`,
        'Accumbo-Client-ID': clientId
      }
    });

    if (!res.ok) {
      const error = new Error('An error occurred while fetching the data.');
      let cause = { url };
      let errorResponse = {};

      try {
        errorResponse = await res.json();
      } catch {
        errorResponse = { body: 'No response data available' };
      }

      cause = { ...cause, ...errorResponse };
      error.cause = cause;
      // @ts-ignore
      error.status = res.status;
      throw error;
    }

    return res.json();
  };

  const { data: caregiverSearchResults } = useSWR<SystemUserSearchResults>(
    hasMedicalManagement
      ? `/admin/users?offset=0&limit=100&q=authorities=in=(caregiver,nurse)&extraFields=authorities`
      : null,
    fetcher
  );

  const activeErrandsParams = new URLSearchParams();
  if (state.isViewingAllErrands) {
    activeErrandsParams.append('includeAll', 'true');
  }
  if (state.isViewingAllErrandsForRole) {
    activeErrandsParams.append('viewAllErrandsForRole', 'true');
  }
  const activeErrandsQuery = activeErrandsParams.toString();

  const appointmentParams = new URLSearchParams();
  if (state.isViewingAllErrands) {
    appointmentParams.append('includeAll', 'true');
  }
  if (state.isViewingAllErrandsForRole) {
    appointmentParams.append('viewAllAppointmentsForRole', 'true');
  }
  const appointmentQuery = appointmentParams.toString();

  const { data: activeErrands } = useSWR<ErrandListV2>(
    hasNextGenAccess ? `/clinic/errand/new${activeErrandsQuery ? `?${activeErrandsQuery}` : ''}` : null,
    fetcher
  );
  const { data: appointmentsToStart } = useSWR<ErrandListV2>(
    hasNextGenAccess ? `/clinic/appointment/new${appointmentQuery ? `?${appointmentQuery}` : ''}` : null,
    fetcher
  );
  const { data: appointmentsOngoingCaregiver } = useSWR<ErrandListV2>(
    hasNextGenAccess ? `/clinic/appointment/ongoing-caregiver${appointmentQuery ? `?${appointmentQuery}` : ''}` : null,
    fetcher
  );
  const { data: sharedQueueErrands } = useSWR<ErrandListV2>(
    hasNextGenAccess
      ? `/clinic/shared-queue/errand/new${!state.isViewingAllSharedQueueErrands ? `?requestedCaregiverId=${user.guid}` : '?includeAll=true'}`
      : null,
    fetcher
  );

  const { data: sharedQueueErrandsOngoing } = useSWR<ErrandListV2>(
    hasNextGenAccess
      ? `/clinic/shared-queue/errand/ongoing-caregiver${!state.isViewingAllSharedQueueErrands ? `?requestedCaregiverId=${user.guid}` : '?includeAll=true'}`
      : null,
    fetcher
  );

  preload('/clinic/appointment/reasons', fetcher);
  preload('/clinic/errand/reasons', fetcher);
  preload('/clinic/shared-queue/errand-type', fetcher);
  preload('/clinic/appointment/activities?owner=PATIENT', fetcher);
  preload('/clinic/appointment/activities?owner=CAREGIVER', fetcher);

  const quickSignErrand = async (patientId: string, encounterId: number) => {
    const encounterSigning: EncounterSigning = {
      createFinalNote: false,
      quickSigned: true
    };

    return fetch(`${basePath}/clinic/encounter/${patientId}/${encounterId}/sign`, {
      // @ts-ignore
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Accumbo-Client-ID': clientId,
        'Content-Type': 'application/json'
      },
      method: 'PUT',
      body: JSON.stringify(encounterSigning)
    });
  };

  const startEncounter = async (patientId: string, encounterId: number) => {
    return fetch(`${basePath}/clinic/shared-queue/${patientId}/${encounterId}/start`, {
      // @ts-ignore
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Accumbo-Client-ID': clientId,
        'Content-Type': 'application/json'
      },
      method: 'PUT'
    });
  };

  const value = {
    fetcher,
    ...state,
    user,
    authorities,
    role,
    numActiveErrands: activeErrands?.page?.totalNumberOfItems || 0,
    numSharedQueueErrands:
      (sharedQueueErrands?.page?.totalNumberOfItems || 0) + (sharedQueueErrandsOngoing?.page?.totalNumberOfItems || 0),
    numActiveAppointments:
      (appointmentsToStart?.page?.totalNumberOfItems || 0) +
      (appointmentsOngoingCaregiver?.page?.totalNumberOfItems || 0),
    caregivers:
      caregiverSearchResults?.members
        ?.filter((m) => m.systemUserExtras.authorities.includes('handleNextGenPatients'))
        .map((m) => ({ ...m.user, authorities: m.systemUserExtras.authorities })) || [],
    setHistoryCutoffDays: (numDays: number) => dispatch({ type: 'setHistoryCutoffDays', numDays }),
    toggleViewingAllErrands: () => dispatch({ type: 'toggleViewingAllErrands' }),
    toggleViewingAllErrandsForRole: () => dispatch({ type: 'toggleViewingAllErrandsForRole' }),
    toggleViewingAllSharedQueueErrands: () => dispatch({ type: 'toggleViewingAllSharedQueueErrands' }),
    selectCaregiverFilter: (caregiver: SystemUser) => dispatch({ type: 'selectCaregiverFilter', caregiver }),
    setFilter: (filter: string, reasons: string[]) => dispatch({ type: 'setFilter', filter, reasons }),
    toggleSortOrder: (section: string) => dispatch({ type: 'setSortOrder', section }),
    setSectionPage: useCallback(
      (section: string, page: number) => dispatch({ type: 'setSectionPage', section, page }),
      []
    ),
    setLastVisitedTab: useCallback((tab: InboxTab) => dispatch({ type: 'setLastVisitedTab', tab }), []),
    quickSignErrand,
    startEncounter,
    toast: showNotification,
    authToken,
    clientId,
    renderCaregiverInitials: state.isViewingAllErrands || state.isViewingAllErrandsForRole
  };

  return <InboxContext.Provider value={value}>{children}</InboxContext.Provider>;
};

const mapStateToProps = (state) => {
  return {
    authToken: state.auth.token.jwt,
    clientId: state.auth.clientId,
    user: state.auth.token.user,
    role: state.auth.isNurse ? 'NURSE' : 'DOCTOR',
    authorities: state.auth.authorities
  };
};

const mapActionsToProps = {
  showNotification
};

export default connect(mapStateToProps, mapActionsToProps)(InboxProvider);

export function useInbox() {
  return useContext(InboxContext);
}
